import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
} from "firebase/storage";
import { storage } from "@/config/firebase";

/**
 * Serviço de Upload para Firebase Storage
 */
class FirebaseStorageService {
  /**
   * Upload de arquivo simples
   * @param {File} file - Arquivo para upload
   * @param {string} path - Caminho no storage (ex: 'products/image.jpg')
   * @returns {Promise<string>} URL pública do arquivo
   */
  async uploadFile(file, path) {
    try {
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log("✅ Upload concluído:", downloadURL);
      return downloadURL;
    } catch (error) {
      console.error("❌ Erro no upload:", error);
      throw error;
    }
  }

  /**
   * Upload de arquivo com progresso
   * @param {File} file - Arquivo para upload
   * @param {string} path - Caminho no storage
   * @param {Function} onProgress - Callback de progresso (percent)
   * @param {object} [metadata] - Metadados do Storage (ex.: { customMetadata: {...} })
   * @returns {Promise<string>} URL pública do arquivo
   */
  async uploadFileWithProgress(file, path, onProgress, metadata) {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(Math.round(progress));
          }
          console.log(`📤 Upload: ${Math.round(progress)}%`);
        },
        (error) => {
          console.error("❌ Erro no upload:", error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log("✅ Upload concluído:", downloadURL);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        },
      );
    });
  }

  /**
   * Upload de imagem de produto
   * @param {File} file - Arquivo de imagem
   * @param {string} productId - ID do produto
   * @param {string} companyCode - empresa dona do arquivo
   * @returns {Promise<string>} URL da imagem
   */
  async uploadProductImage(file, productId, companyCode) {
    const timestamp = Date.now();
    const fileName = `${productId}_${timestamp}_${file.name}`;
    const path = `${this.companyFolder(companyCode)}products/${fileName}`;
    return this.uploadFile(file, path);
  }

  /**
   * Upload de imagem de categoria
   * @param {File} file - Arquivo de imagem
   * @param {string} categoryId - ID da categoria
   * @param {string} companyCode - empresa dona do arquivo
   * @returns {Promise<string>} URL da imagem
   */
  async uploadCategoryImage(file, categoryId, companyCode) {
    const timestamp = Date.now();
    const fileName = `${categoryId}_${timestamp}_${file.name}`;
    const path = `${this.companyFolder(companyCode)}categories/${fileName}`;
    return this.uploadFile(file, path);
  }

  /**
   * Upload de banner
   * @param {File} file - Arquivo de imagem
   * @param {string} companyCode - empresa dona do arquivo
   * @returns {Promise<string>} URL da imagem
   */
  async uploadBanner(file, companyCode) {
    const timestamp = Date.now();
    const fileName = `banner_${timestamp}_${file.name}`;
    const path = `${this.companyFolder(companyCode)}banners/${fileName}`;
    return this.uploadFile(file, path);
  }

  /**
   * Deletar arquivo do storage
   * @param {string} fileURL - URL completa do arquivo
   * @returns {Promise<void>}
   */
  async deleteFile(fileURL) {
    try {
      // Extrair o path do storage da URL
      const path = this.extractPathFromURL(fileURL);
      if (!path) {
        throw new Error("URL inválida");
      }

      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      console.log("🗑️ Arquivo deletado:", path);
    } catch (error) {
      console.error("❌ Erro ao deletar:", error);
      throw error;
    }
  }

  /**
   * Listar todos os arquivos de uma pasta
   * @param {string} folderPath - Caminho da pasta (ex: 'products/')
   * @returns {Promise<string[]>} Array de URLs
   */
  async listFiles(folderPath) {
    try {
      const folderRef = ref(storage, folderPath);
      const result = await listAll(folderRef);

      const urls = await Promise.all(
        result.items.map((itemRef) => getDownloadURL(itemRef)),
      );

      return urls;
    } catch (error) {
      console.error("❌ Erro ao listar arquivos:", error);
      throw error;
    }
  }

  /**
   * Prefixo da empresa no Storage ({companyCode}/): nenhum arquivo fica
   * fora da pasta de uma empresa
   * @param {string} companyCode
   * @returns {string}
   */
  companyFolder(companyCode) {
    const code = String(companyCode || "").trim();
    return `${code || "sem-empresa"}/`;
  }

  /**
   * Pasta de uploads do usuário dentro da empresa a que ele está vinculado:
   * uploads/{company_code}/{user_id}/
   * @param {object} user - usuário logado (useAuth): { id, company_code }
   * @returns {string|null} null se o usuário não tiver empresa ou id
   */
  getUserUploadFolder(user) {
    const companyCode = String(user?.company_code || "").trim();
    const userId = String(user?.id ?? "").trim();
    if (!companyCode || !userId) return null;
    return `uploads/${companyCode}/${userId}/`;
  }

  /**
   * Listar arquivos de uma pasta com URL e metadados
   * @param {string} folderPath - Caminho da pasta (ex: 'uploads/{empresa}/{usuario}/')
   * @returns {Promise<Array<{name, fullPath, url, size, contentType, timeCreated, customMetadata}>>}
   */
  async listFilesWithMetadata(folderPath) {
    try {
      const folderRef = ref(storage, folderPath);
      const result = await listAll(folderRef);

      return await Promise.all(
        result.items.map(async (itemRef) => {
          const [url, metadata] = await Promise.all([
            getDownloadURL(itemRef),
            getMetadata(itemRef).catch(() => ({})),
          ]);
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            url,
            size: metadata.size,
            contentType: metadata.contentType,
            timeCreated: metadata.timeCreated,
            customMetadata: metadata.customMetadata || {},
          };
        }),
      );
    } catch (error) {
      console.error("❌ Erro ao listar arquivos:", error);
      throw error;
    }
  }

  /**
   * Extrair path do Firebase Storage de uma URL
   * @param {string} url - URL completa do Firebase Storage
   * @returns {string|null} Path do arquivo
   */
  extractPathFromURL(url) {
    try {
      const match = url.match(/\/o\/(.+?)\?/);
      if (match && match[1]) {
        return decodeURIComponent(match[1]);
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Validar se arquivo é uma imagem
   * @param {File} file - Arquivo para validar
   * @returns {boolean}
   */
  isValidImage(file) {
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    return validTypes.includes(file.type);
  }

  /**
   * Validar tamanho do arquivo (padrão: 5MB)
   * @param {File} file - Arquivo para validar
   * @param {number} maxSizeMB - Tamanho máximo em MB
   * @returns {boolean}
   */
  isValidSize(file, maxSizeMB = 5) {
    const maxBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxBytes;
  }

  /**
   * Validar arquivo antes do upload
   * @param {File} file - Arquivo para validar
   * @param {number} maxSizeMB - Tamanho máximo em MB
   * @throws {Error} Se arquivo for inválido
   */
  validateFile(file, maxSizeMB = 5) {
    if (!file) {
      throw new Error("Nenhum arquivo selecionado");
    }

    if (!this.isValidImage(file)) {
      throw new Error("Formato de imagem inválido. Use JPG, PNG, GIF ou WEBP");
    }

    if (!this.isValidSize(file, maxSizeMB)) {
      throw new Error(`Arquivo muito grande. Máximo: ${maxSizeMB}MB`);
    }

    return true;
  }
}

// Exportar instância singleton
const firebaseStorageService = new FirebaseStorageService();
export default firebaseStorageService;

// Exportar também métodos individuais para facilitar uso
export const {
  uploadFile,
  uploadFileWithProgress,
  uploadProductImage,
  uploadCategoryImage,
  uploadBanner,
  deleteFile,
  listFiles,
  validateFile,
  isValidImage,
  isValidSize,
} = firebaseStorageService;
